import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper, Typography, Box, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, TableSortLabel, Tooltip,
  LinearProgress
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import logo from '../../assets/images/Supervoima-Logo-White.svg';

// Baseline for progress tier calculation (internal use only, not displayed)
const BASELINE_QUESTIONS = 39;

// Progress tier configuration
const getProgressTier = (percentage) => {
  if (percentage >= 100) {
    return {
      label: 'Comprehensive AI Profile',
      header: 'COMPREHENSIVE PROFILE',
      color: '#2E7D32', // Green
      bodyText: (count) => `Outstanding! You've answered all available questions. Answer more questions to track your AI maturity progress and trends over time.`
    };
  }
  if (percentage > 75) {
    return {
      label: 'Broad AI Profile',
      header: 'BROAD PROFILE',
      color: '#2E7D32', // Green
      bodyText: (count) => `Excellent work! Your ${count} responses have created a rich, detailed profile. Answer more questions to track your AI maturity progress and trends over time.`
    };
  }
  if (percentage >= 50) {
    return {
      label: 'Maturing AI Profile',
      header: 'MATURING PROFILE',
      color: '#43A047', // Green
      bodyText: (count) => `Your ${count} responses are building a detailed picture of your AI maturity. The insights are becoming more personalized. Additional responses will fine-tune your results.`
    };
  }
  if (percentage >= 25) {
    return {
      label: 'Basic AI Profile',
      header: 'BASIC PROFILE',
      color: '#ED6C02', // Orange
      bodyText: (count) => `You've established a solid foundation with ${count} responses. Your profile is taking shape. Continue to enrich your insights and unlock more detailed recommendations.`
    };
  }
  // < 25%
  return {
    label: 'Incomplete AI Profile',
    header: 'INCOMPLETE PROFILE',
    color: '#D32F2F', // Red
    bodyText: (count) => `You've answered ${count} questions. Continue to unlock meaningful insights about your AI maturity.`
  };
};

// Check if responses are stale (>90 days since last response)
const STALE_THRESHOLD_DAYS = 90;
const isResponsesStale = (latestTimestamp) => {
  if (!latestTimestamp) return false;
  const lastResponseDate = new Date(latestTimestamp);
  const daysSinceLastResponse = Math.floor((Date.now() - lastResponseDate.getTime()) / (1000 * 60 * 60 * 24));
  return daysSinceLastResponse > STALE_THRESHOLD_DAYS;
};

const getDaysSinceLastResponse = (latestTimestamp) => {
  if (!latestTimestamp) return null;
  const lastResponseDate = new Date(latestTimestamp);
  return Math.floor((Date.now() - lastResponseDate.getTime()) / (1000 * 60 * 60 * 24));
};

// Assessment Progress Section Component
function AssessmentProgressSection({ questionCount, latestTimestamp, navigate }) {
  const percentage = Math.min(100, Math.round((questionCount / BASELINE_QUESTIONS) * 100));
  const tier = getProgressTier(percentage);
  const stale = isResponsesStale(latestTimestamp);
  const daysSince = getDaysSinceLastResponse(latestTimestamp);

  return (
    <Paper elevation={4} sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
      {/* Tier Icon for Complete/Comprehensive state */}
      {percentage >= 75 && (
        <CheckCircleOutlineIcon
          sx={{ fontSize: 48, color: tier.color, mb: 2 }}
        />
      )}

      {/* Dynamic Header */}
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          mb: 2,
          color: 'rgb(1, 28, 45)',
          fontFamily: 'Bebas Neue',
          fontWeight: 'normal'
        }}
      >
        {tier.header}
      </Typography>

      {/* Progress Indicator */}
      <Box sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
        <Typography variant="body2" sx={{ color: tier.color, fontWeight: 'medium', mb: 1 }}>
          {tier.label}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: '#c0c0c0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: tier.color,
              borderRadius: 5
            }
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {questionCount} responses
        </Typography>
      </Box>

      {/* Stale Responses Warning */}
      {stale && daysSince && (
        <Box sx={{
          backgroundColor: '#FFF3E0',
          border: '1px solid #FFB74D',
          borderRadius: 2,
          p: 2,
          mb: 3,
          maxWidth: 500,
          mx: 'auto'
        }}>
          <Typography variant="body2" color="warning.dark">
            Your last response was {daysSince} days ago. Answer more questions to keep your profile current and track your AI maturity progress and trends.
          </Typography>
        </Box>
      )}

      {/* Tier-Based Body Text */}
      <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
        {tier.bodyText(questionCount)}
      </Typography>

      {/* Single CTA Button - Always shown for trend tracking */}
      <Button
        variant="contained"
        size="large"
        onClick={() => {
          window.scrollTo(0, 0);
          navigate('/interview');
        }}
        sx={{
          backgroundColor: 'rgb(1, 28, 45)',
          color: 'white',
          borderRadius: '20px',
          '&:hover': {
            backgroundColor: 'rgb(1, 48, 75)'
          },
          minWidth: '200px',
          textTransform: 'none'
        }}
      >
        Continue Assessment
      </Button>
    </Paper>
  );
}

export default function QuestionsSection({ sessionData, companyName = '', userName = '' }) {
  const navigate = useNavigate();

  const getHeaderText = () => {
    return 'AI Questions';
  };
  const [orderBy, setOrderBy] = useState('priority');
  const [order, setOrder] = useState('desc');

  // Process session data - backend returns deduplicated responses (one per question, newest)
  const allQuestionsData = useMemo(() => {
    if (!sessionData?.responses) return [];

    return sessionData.responses.map(response => {
      // Calculate priority using same formula as org reports: unmet_need * importance
      const unmetNeed = Math.max(0, (response.importance || 0) - (response.score || 0));
      const priority = unmetNeed * (response.importance || 0);

      return {
        ...response,
        sessionId: response.session_id || sessionData.sessionId,
        date: new Date(response.timestamp).toLocaleDateString('fi-FI'),
        dateTimestamp: new Date(response.timestamp).getTime(),
        priority: priority
      };
    });
  }, [sessionData]);

  // Calculate max priority score for percentage calculation
  const maxPriorityScore = useMemo(() => {
    if (allQuestionsData.length === 0) return 0;
    return Math.max(...allQuestionsData.map(q => q.priority || 0));
  }, [allQuestionsData]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedQuestions = useMemo(() => {
    return [...allQuestionsData].sort((a, b) => {
      if (orderBy === 'priority') {
        return order === 'desc' ? (b.priority || 0) - (a.priority || 0) : (a.priority || 0) - (b.priority || 0);
      }
      if (orderBy === 'score') {
        return order === 'desc' ? (b.score || 0) - (a.score || 0) : (a.score || 0) - (b.score || 0);
      }
      if (orderBy === 'importance') {
        return order === 'desc' ? (b.importance || 0) - (a.importance || 0) : (a.importance || 0) - (b.importance || 0);
      }
      if (orderBy === 'date') {
        return order === 'desc' ? (b.dateTimestamp || 0) - (a.dateTimestamp || 0) : (a.dateTimestamp || 0) - (b.dateTimestamp || 0);
      }
      return 0;
    });
  }, [allQuestionsData, orderBy, order]);

  if (!sessionData || !allQuestionsData.length) {
    return null;
  }

  return (
    <>
      {/* Comprehensive Questions Table */}
      <Paper elevation={4} sx={{ p: 0, mb: 6, borderRadius: 3, overflow: 'hidden' }}>
        {/* Blue Header Bar with Logo and Title */}
        <Box sx={{
          backgroundColor: 'rgb(1, 28, 45)',
          height: '60px',
          display: 'flex',
          alignItems: 'center',
          px: 3
        }}>
          <img
            src={logo}
            alt="Supervoima Logo"
            style={{ height: '36px', width: 'auto', marginRight: '16px' }}
          />
          <Typography variant="h5" sx={{
            color: 'white',
            fontFamily: 'Bebas Neue',
            fontWeight: 'normal',
            textAlign: 'center',
            flexGrow: 1
          }}>
            {getHeaderText()}
          </Typography>
        </Box>

        {/* Content Area */}
        <Box sx={{ p: 5 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 4, color: 'rgb(1, 28, 45)', fontFamily: 'Bebas Neue', fontWeight: 'normal' }}>
            QUESTIONS SUMMARY
          </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Complete overview of all answered questions across all sessions with sortable columns.
        </Typography>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100', textAlign: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'priority'}
                    direction={orderBy === 'priority' ? order : 'asc'}
                    onClick={() => handleRequestSort('priority')}
                  >
                    Priority
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>Question</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>Latest Answer</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100', minWidth: 100 }}>
                  <TableSortLabel
                    active={orderBy === 'date'}
                    direction={orderBy === 'date' ? order : 'asc'}
                    onClick={() => handleRequestSort('date')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100', textAlign: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'score'}
                    direction={orderBy === 'score' ? order : 'asc'}
                    onClick={() => handleRequestSort('score')}
                  >
                    Score
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100', textAlign: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'importance'}
                    direction={orderBy === 'importance' ? order : 'asc'}
                    onClick={() => handleRequestSort('importance')}
                  >
                    Importance
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedQuestions.map((question) => {
                const priorityPercentage = maxPriorityScore > 0 ? Math.round((question.priority || 0) / maxPriorityScore * 100) : 0;

                return (
                  <TableRow
                    key={question.question_id}
                    hover
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                      '&:hover': { backgroundColor: 'action.selected' }
                    }}
                  >
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={(question.priority || 0) >= 12.0 ? 'HIGH' : (question.priority || 0) >= 6.0 ? 'MEDIUM' : 'LOW'}
                          size="small"
                          color={(question.priority || 0) >= 12.0 ? 'error' : (question.priority || 0) >= 6.0 ? 'warning' : 'success'}
                          sx={{
                            fontWeight: 'bold',
                            color: 'white',
                            fontSize: '0.7rem'
                          }}
                        />
                        <Tooltip title="Priority % relative to identified top priority" arrow>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 'medium',
                              color: (question.priority || 0) >= 12.0 ? 'error.main' : (question.priority || 0) >= 6.0 ? 'warning.main' : 'success.main',
                              cursor: 'help'
                            }}
                          >
                            {priorityPercentage}%
                          </Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {question.question_text}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 400 }}>
                    <Typography variant="body2" sx={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {question.user_answer || 'No answer recorded'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {question.date}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={question.score} 
                      size="small"
                      color={question.score >= 4 ? 'success' : question.score >= 2.5 ? 'warning' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={question.importance} 
                      size="small"
                      color={question.importance >= 4 ? 'success' : question.importance >= 2.5 ? 'warning' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      </Paper>

      {/* Assessment Progress Section */}
      <AssessmentProgressSection
        questionCount={sortedQuestions.length}
        latestTimestamp={sortedQuestions.length > 0
          ? Math.max(...sortedQuestions.map(q => q.dateTimestamp || 0))
          : null
        }
        navigate={navigate}
      />
    </>
  );
}
